// 工作台卡片列表 / Workbench card list.
// 渲染 Demo 与用户 Task 卡片，并维护当前选中的卡片。
// Renders Demo and user Task cards and owns the current card selection.

import type { DemoABEntry, DemoSingleEntry, Mode, TaskListItem } from "@/lib/types";
import { $maybe, escapeHtml } from "@/lib/dom";
import { formatConfigLabel, type ConfigKind } from "@/lib/config-labels";
import { createCardSkeletonGrid } from "@/lib/spinner";
import { state } from "@/state/store";

// 卡片详情目标 / Target payload for Details button callbacks.
export type CardDetailsTarget =
  | { kind: "demo"; id: string; mode: Mode }
  | { kind: "task"; id: string; mode: Mode };

interface CardListDeps {
  onDetails?: (target: CardDetailsTarget) => void;
}

let cardListDeps: CardListDeps = {};
let cardListEvents: AbortController | null = null;

/**
 * 初始化卡片列表事件 / Initialize card-list event handlers.
 */
export function initCardList(deps: CardListDeps = {}): void {
  cardListDeps = deps;
  cardListEvents?.abort();
  cardListEvents = new AbortController();
  const { signal } = cardListEvents;
  window.addEventListener("ripple:demos-loaded", () => renderCardList(), { signal });
  window.addEventListener("ripple:demos-failed", (event) => {
    const detail = (event as CustomEvent<{ message?: string }>).detail;
    renderCardListError(`Demo catalog unavailable: ${detail?.message ?? "unknown error"}`);
  }, { signal });
  window.addEventListener("ripple:mode-changed", () => renderCardList(), { signal });
  window.addEventListener("ripple:language-changed", () => renderCardList(), { signal });
  renderCardList();
}

/**
 * 渲染当前视图卡片 / Render cards for the current workbench view.
 */
export function renderCardList(): void {
  const root = $maybe("card-workspace");
  if (!root) return;
  root.replaceChildren();

  const header = document.createElement("div");
  header.className = "card-workspace-head";
  header.innerHTML = `
    <div>
      <p class="eyebrow">${state.workbenchView === "demo" ? "Demo Library" : "My Tasks"}</p>
      <h2>${state.mode === "ab" ? "A/B PMF" : "Single"} ${state.workbenchView === "demo" ? "Demos" : "Tasks"}</h2>
    </div>
  `;

  const grid = document.createElement("div");
  grid.className = "workbench-card-grid";
  root.append(header, grid);

  if (state.workbenchView === "tasks") {
    renderTaskCards(grid);
    return;
  }
  renderDemoCards(grid);
}

/**
 * 渲染卡片区错误态 / Render a card-workspace error state.
 */
export function renderCardListError(message: string): void {
  const root = $maybe("card-workspace");
  if (!root) return;
  root.replaceChildren();
  const error = document.createElement("p");
  error.className = "workbench-error";
  error.textContent = message;
  root.append(error);
}

/**
 * 渲染卡片区骨架 / Render a card-workspace loading skeleton.
 * 服务于模式切换 / 首屏 demos 加载 / My Tasks 加载时的"立即占位"体验。
 * Serves mode-switches, initial demo loads, and My Tasks loads so the
 * workspace paints immediately instead of flashing empty or blocking.
 */
export function renderCardListSkeleton(
  kind: "demo" | "tasks" = state.workbenchView,
  count = 4,
): void {
  const root = $maybe("card-workspace");
  if (!root) return;
  root.replaceChildren();

  const header = document.createElement("div");
  header.className = "card-workspace-head";
  header.innerHTML = `
    <div>
      <p class="eyebrow">${kind === "demo" ? "Demo Library" : "My Tasks"}</p>
      <h2>${state.mode === "ab" ? "A/B PMF" : "Single"} ${kind === "demo" ? "Demos" : "Tasks"}</h2>
    </div>
  `;
  root.append(header, createCardSkeletonGrid(count));
}

// 渲染 Demo 卡片 / Render Demo cards.
function renderDemoCards(grid: HTMLElement): void {
  const demos = currentModeDemos();
  if (!demos.length) {
    renderEmpty(grid, "No demos available for this mode.");
    state.currentTask = null;
    return;
  }

  const selected = selectedDemoId();
  const nextSelected = selected && demos.some((demo) => demo.id === selected)
    ? selected
    : demos[0]!.id;
  setSelectedDemo(nextSelected);

  for (const demo of demos) {
    grid.append(createDemoCard(demo));
  }
}

// 渲染用户 Task 卡片 / Render user Task cards.
function renderTaskCards(grid: HTMLElement): void {
  const tasks = sortedCurrentModeTasks();
  if (!tasks.length) {
    renderEmpty(grid, "No tasks available for this mode.");
    state.selectedTaskId = null;
    return;
  }

  const selected = state.selectedTaskId;
  state.selectedTaskId =
    selected && tasks.some((task) => task.id === selected) ? selected : tasks[0]!.id;

  for (const task of tasks) {
    grid.append(createTaskCard(task));
  }
}

// 当前模式 Task 按创建时间倒序 / Current-mode Tasks sorted newest-first.
function sortedCurrentModeTasks(): TaskListItem[] {
  return state.tasks
    .filter((task) => task.mode === state.mode)
    .sort(compareTaskCreatedNewestFirst);
}

// 比较 Task 创建时间 / Compare Task creation time.
function compareTaskCreatedNewestFirst(left: TaskListItem, right: TaskListItem): number {
  const delta = taskCreatedTime(right) - taskCreatedTime(left);
  return delta !== 0 ? delta : left.id.localeCompare(right.id);
}

// 解析 Task 创建时间 / Parse Task creation time.
function taskCreatedTime(task: TaskListItem): number {
  const time = Date.parse(task.created_at);
  return Number.isNaN(time) ? 0 : time;
}

// 创建 Demo 卡片 / Create one Demo card.
function createDemoCard(demo: DemoSingleEntry | DemoABEntry): HTMLElement {
  const active = demo.id === selectedDemoId();
  const shell = document.createElement("article");
  shell.className = "workbench-card-shell";
  if (active) shell.classList.add("active");
  const card = document.createElement("button");
  card.type = "button";
  card.className = "workbench-card";
  card.dataset.cardKind = "demo";
  card.dataset.cardId = demo.id;
  card.setAttribute("aria-pressed", String(active));
  if (active) card.classList.add("active");
  card.addEventListener("click", () => selectDemoCard(demo.id));

  const planCount = state.mode === "ab" ? "2 Plans" : "1 Plan";
  card.innerHTML = `
    <div class="workbench-card-main">
      <span class="workbench-card-kicker">${state.mode === "ab" ? "A/B PMF" : "Single"} · ${planCount}</span>
      <strong>${escapeHtml(demo.title || demo.id)}</strong>
      <p>${escapeHtml(demo.description || demo.id)}</p>
      <div class="workbench-tag-row">
        ${renderConfigTags((demo.defaults ?? {}) as Record<string, unknown>)}
        ${demo.report_slug ? "<span>Report</span>" : ""}
      </div>
    </div>
  `;
  shell.append(card, createDetailsButton({ kind: "demo", id: demo.id, mode: state.mode }));
  return shell;
}

// 创建 Task 卡片 / Create one Task card.
function createTaskCard(task: TaskListItem): HTMLElement {
  const active = task.id === state.selectedTaskId;
  const simulationActive = isTaskSimulationActive(task);
  const shell = document.createElement("article");
  shell.className = "workbench-card-shell";
  if (active) shell.classList.add("active");
  if (simulationActive) shell.classList.add("is-simulation-active");
  const card = document.createElement("button");
  card.type = "button";
  card.className = "workbench-card";
  card.dataset.cardKind = "task";
  card.dataset.cardId = task.id;
  card.setAttribute("aria-pressed", String(active));
  if (active) card.classList.add("active");
  card.addEventListener("click", () => selectTaskCard(task.id));

  card.innerHTML = `
    <div class="workbench-card-main">
      <span class="workbench-card-topline">
        <span class="workbench-card-kicker">${task.mode === "ab" ? "A/B PMF" : "Single"} · Task</span>
        ${simulationActive ? renderTaskSimulationBadge(task) : ""}
      </span>
      <strong>${escapeHtml(task.title || task.id)}</strong>
      <p>${escapeHtml(task.plan_excerpt || "No plan excerpt yet.")}</p>
      <div class="workbench-tag-row">
        ${renderConfigTags(task.config)}
        <span>${task.simulation_count} runs</span>
        <span>${task.ready_report_count} reports</span>
      </div>
    </div>
  `;
  shell.append(card, createDetailsButton({ kind: "task", id: task.id, mode: task.mode }));
  return shell;
}

// Task 是否有活跃模拟 / Whether the Task has an active simulation.
function isTaskSimulationActive(task: TaskListItem): boolean {
  return task.latest_simulation?.status === "queued" || task.latest_simulation?.status === "running";
}

// Task 活跃模拟标识 / Active simulation badge for a Task card.
function renderTaskSimulationBadge(task: TaskListItem): string {
  const label = task.latest_simulation?.status === "queued" ? "Queued" : "Running";
  return `
    <span class="workbench-card-status" aria-label="Simulation ${label}">
      <span class="workbench-card-status-dot" aria-hidden="true"></span>
      ${escapeHtml(label)}
    </span>
  `;
}

// 创建详情按钮 / Create the Details button.
function createDetailsButton(target: CardDetailsTarget): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "button ghost workbench-details-button";
  button.dataset.detailsId = target.id;
  button.textContent = "Details";
  button.addEventListener("click", (event) => {
    event.stopPropagation();
    cardListDeps.onDetails?.(target);
  });
  return button;
}

// 选择 Demo 卡片 / Select a Demo card.
function selectDemoCard(id: string): void {
  setSelectedDemo(id);
  renderCardList();
  dispatchSelectionChanged();
}

// 选择 Task 卡片 / Select a Task card.
function selectTaskCard(id: string): void {
  state.selectedTaskId = id;
  renderCardList();
  dispatchSelectionChanged();
}

// 当前模式 Demo 列表 / Demos for the active mode.
function currentModeDemos(): Array<DemoSingleEntry | DemoABEntry> {
  return state.mode === "ab" ? state.demos.ab : state.demos.single;
}

// 当前模式已选 Demo id / Selected Demo id for the active mode.
function selectedDemoId(): string | null {
  return state.mode === "ab" ? state.selectedDemo.ab : state.selectedDemo.single;
}

// 保存当前模式 Demo 选择 / Save selected Demo id for the active mode.
function setSelectedDemo(id: string): void {
  if (state.mode === "ab") {
    state.selectedDemo.ab = id;
    state.selectedDemo.single = null;
  } else {
    state.selectedDemo.single = id;
    state.selectedDemo.ab = null;
  }
  const demo = currentModeDemos().find((item) => item.id === id);
  state.currentTask = {
    kind: "demo",
    mode: state.mode,
    demoId: id,
    reportSlug: demo?.report_slug ?? null,
  };
}

// 渲染配置标签（中文展示）/ Render config tags in Chinese labels.
// 与 Demo 浮层、Task 浮层 tag 共用 formatConfigLabel；未知值展示为"自定义 X"，
// 原始 enum 放进 title 便于排查，但视觉上 Demo / Task 卡片不再出现裸英文 enum。
// Shares formatConfigLabel with the Demo / Task modals so all tags are Chinese.
// Unknown values fall back to "自定义 X"; the raw enum is kept in title for debugging.
function renderConfigTags(config: Record<string, unknown>): string {
  return (["channel", "platform", "vertical"] as const satisfies readonly ConfigKind[])
    .map((key) => {
      const raw = String(config[key] ?? "").trim();
      if (!raw) return "";
      const label = formatConfigLabel(key, raw);
      const title = raw !== label ? ` title="${escapeHtml(raw)}"` : "";
      return `<span${title}>${escapeHtml(label)}</span>`;
    })
    .filter(Boolean)
    .join("");
}

// 渲染空状态 / Render empty state.
function renderEmpty(root: HTMLElement, message: string): void {
  const empty = document.createElement("p");
  empty.className = "workbench-empty";
  empty.textContent = message;
  root.append(empty);
}

// 通知工具栏刷新 / Notify toolbar consumers to refresh.
function dispatchSelectionChanged(): void {
  window.dispatchEvent(new CustomEvent("ripple:card-selection-changed"));
}
